import '../text-and-lang/as-text.ts';
import styleText from './numbox.css?raw';
import type { SetValueOptions } from './types';
import { resolveSetValueSilence } from './utils';

/**
 * # NumberValueBox Component
 * 
 * A versatile numeric input component that provides multiple ways to interact with numeric values.
 * Features an input field, optional increment/decrement controls, and an optional slider for visual manipulation.
 * 
 * ## Features
 * - Direct numeric input with validation
 * - Increment/decrement buttons with customizable step values
 * - Optional slider for visual value selection
 * - Unit display support
 * - Customizable decimal precision
 * - Number padding (zeros or spaces)
 * - Min/max value constraints
 * - Keyboard navigation (arrow keys, page up/down)
 * - Mouse wheel support for value adjustment
 * - Event throttling with cooloff periods
 * - Localized label support
 * - Paramate integration support
 * - Shadow DOM encapsulation option
 * 
 * ## Component Hierarchy
 * ```
 * NumberValueBox (as-numbox)
 * ├── label - Optional localized label
 * ├── wrapper
 * │   ├── container - Main input container
 * │   │   ├── input[type="number"] - The numeric input field
 * │   │   └── controls - Increment/decrement buttons
 * │   │       ├── increment (▲)
 * │   │       └── decrement (▼)
 * │   └── unit - Unit display span
 * └── slider-container - Optional slider input
 *     └── input[type="range"] - Range slider
 * ```
 * 
 * ## Attributes
 * - `value`: The numeric value (default: 0)
 * - `unit`: Unit text to display after the value
 * - `decimals`: Number of decimal places to display (default: 2)
 * - `padding`: Padding type for the displayed value ('none', 'zeros', 'spaces')
 * - `step`: Increment/decrement step size (default: 1)
 * - `min`: Minimum allowed value (default: -Infinity)
 * - `max`: Maximum allowed value (default: Infinity)
 * - `pad-length`: Total length for padding when using 'zeros' or 'spaces' padding
 * - `show-slider`: When present, displays the slider control
 * - `label`: Label text to display above the input
 * - `label-dict-key`: Localization key for the label
 * - `label-default-text`: Default label text when localization key is not found
 * - `align`: Text alignment in input field ('left' or 'right', default: 'right')
 * - `paramate-paramname` (legacy `paramate-paramName` also supported): Parameter name for Paramate integration
 * - `cooloff`: Cooloff period in milliseconds for event throttling (default: 0)
 * - `shadow-dom`: When present, uses Shadow DOM for encapsulation
 * - `show-arrow-buttons`: When present, shows the increment/decrement arrow buttons (default: hidden)
 * 
 * ## CSS Custom Properties
 * - `--as-font-family-base`: Font family for the component
 * - `--as-primary`: Primary color for slider thumb (default: #007acc)
 * - `--as-text-secondary`: Secondary text color for labels (default: #666)
 * - `--numbox-label-gap`: Gap between label and inputs (default: 4px)
 * - `--numbox-padding`: Component padding (default: 4px)
 * - `--numbox-padding-right`: Right padding (default: 1rem)
 * - `--numbox-border-radius`: Border radius (default: 6px)
 * - `--numbox-container-border-radius`: Input container border radius (default: 1rem)
 * - `--numbox-container-border-color`: Container border color (default: #ccc)
 * - `--numbox-container-border-width`: Container border width (default: 2px)
 * - `--numbox-container-bg`: Container background color (default: white)
 * - `--numbox-container-padding`: Container padding (default: 4px 8px)
 * - `--numbox-focus-color`: Focus outline color (default: #007acc)
 * - `--numbox-focus-width`: Focus outline width (default: 2px)
 * - `--numbox-text-color`: Text color (default: inherit)
 * - `--numbox-unit-color`: Unit text color (default: #666)
 * - `--numbox-control-btn-size`: Control button size (default: 16px x 12px)
 * - `--numbox-control-btn-bg`: Control button background (default: #f5f5f5)
 * - `--numbox-control-btn-border`: Control button border (default: 1px solid #ccc)
 * - `--numbox-control-btn-hover-bg`: Control button hover background (default: #e5e5e5)
 * - `--numbox-control-btn-active-bg`: Control button active background (default: #ddd)
 * - `--numbox-control-btn-radius`: Control button border radius (default: 2px)
 * - `--numbox-slider-height`: Slider height (default: 4px)
 * - `--numbox-slider-bg`: Slider track background (default: #ddd)
 * - `--numbox-slider-thumb-size`: Slider thumb size (default: 12px)
 * - `--numbox-slider-thumb-color`: Slider thumb color (default: var(--as-primary, #007acc))
 * - `--numbox-label-font-size`: Label font size (default: 0.85em)
 * - `--numbox-label-color`: Label color (default: var(--as-text-secondary, #666))
 * - `--numbox-label-height`: Label height (default: 1.2em)
 * - `--numbox-input-width`: Fixed width for the input field (overrides auto-sizing)
 * 
 * ## Events
 * ### Emitted Events
 * - `as-input-input`: Dispatched during value changes (with throttling if cooloff > 0)
 *   - Detail: `{ type: 'number', value: number, oldValue: number, formattedValue: string, phase: 'input' }`
 * - `as-input-change`: Dispatched when value changes are finalized
 *   - Detail: `{ type: 'number', value: number, oldValue: number, formattedValue: string, phase: 'change' }`
 * - Legacy aliases `as-numbox-input` / `as-numbox-change` are still emitted for backward compatibility
 * 
 * ## Usage Examples
 * 
 * ### Basic Usage
 * ```html
 * <as-numbox value="10" unit="px" decimals="0"></as-numbox>
 * ```
 * 
 * ### With Slider and Range
 * ```html
 * <as-numbox 
 *   value="50" 
 *   min="0" 
 *   max="100" 
 *   step="5" 
 *   show-slider>
 * </as-numbox>
 * ```
 * 
 * ### With Localized Label
 * ```html
 * <as-numbox 
 *   value="25.5" 
 *   unit="%" 
 *   label-dict-key="opacity"
 *   label-default-text="Opacity">
 * </as-numbox>
 * ```
 * 
 * ### With Padding and Precision
 * ```html
 * <as-numbox 
 *   value="7" 
 *   decimals="3" 
 *   padding="zeros" 
 *   pad-length="8">
 * </as-numbox>
 * ```
 * 
 * ### With Event Throttling
 * ```html
 * <as-numbox 
 *   value="0" 
 *   cooloff="300">
 * </as-numbox>
 * ```
 * 
 * ### Handling Events
 * ```javascript
 * const numbox = document.querySelector('as-numbox');
 * 
 * numbox.addEventListener('as-numbox-input', (e) => {
 *   console.log('Value changing:', e.detail.value);
 * });
 * 
 * numbox.addEventListener('as-numbox-change', (e) => {
 *   console.log('Final value:', e.detail.value);
 *   // Save or process the final value
 * });
 * ```
 * 
 * ## Interaction Methods
 * - **Keyboard**: Arrow up/down (±1 step), Page up/down (±10 steps)
 * - **Mouse**: Click increment/decrement buttons, wheel scroll over input
 * - **Touch**: Direct input editing, slider manipulation
 * - **Programmatic**: Set `value` property or `value` attribute
 * 
 * ## Implementation Notes
 * - Automatically clamps values to min/max range
 * - Supports both Shadow DOM and light DOM rendering
 * - Integrates with Paramate library for parameter management
 * - Input field auto-resizes based on content width
 * - Slider width matches input container width when no unit is present
 * - Event throttling prevents excessive event firing during rapid changes
 * - Supports external value changes through property setters
 * - Number input arrows are hidden while maintaining numeric input validation
 */

type NumberValueEventDetailBase = {
  type: 'number';
  value: number;
  oldValue: number;
  formattedValue: string;
};

type NumberValueInputDetail = NumberValueEventDetailBase & { phase: 'input' };
type NumberValueChangeDetail = NumberValueEventDetailBase & { phase: 'change' };

interface NumberValueBoxChangeEvent extends CustomEvent<NumberValueChangeDetail> {
  detail: NumberValueChangeDetail;
}

interface NumberValueBoxInputEvent extends CustomEvent<NumberValueInputDetail> {
  detail: NumberValueInputDetail;
}

type PaddingType = 'none' | 'zeros' | 'spaces';

// Template function for rendering the label content
const renderLabelContent = (text: string | null, dictKey: string | null, defaultText: string | null) => {
  if (dictKey) {
    return `<as-text dict-key="${dictKey}"${defaultText ? ` default-text="${defaultText}"` : ''}>${text ?? defaultText ?? ''}</as-text>`;
  } else if (text || defaultText) {
    return `<as-text>${text || defaultText || ''}</as-text>`;
  }
  return '';
};

/**
 * NumberValueBox custom element implementation
 * Provides a comprehensive numeric input component with multiple interaction methods
 */
class NumberValueBox extends HTMLElement {
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();
  private static lightStyleAppended = false;
  private _value: number = 0;
  private _unit: string = '';
  private _decimals: number = 2;
  private _padding: PaddingType = 'none';
  private _step: number = 0.01;
  private _min: number = -Infinity;
  private _max: number = Infinity;
  private _padLength: number = 0;
  private _showSlider: boolean = false;
  private _label: string = '';
  private _align: 'left' | 'right' = 'right';
  private _cooloff: number = 0;
  private _inputCooloffTimer: number | null = null;
  private _changeCooloffTimer: number | null = null;
  private _isInInputCooloff: boolean = false;
  private _isInChangeCooloff: boolean = false;
  private _hasQueuedInputEvent: boolean = false;
  private _hasQueuedChangeEvent: boolean = false;
  private _queuedEventDetail: NumberValueEventDetailBase | null = null;
  private _useShadowDOM: boolean = false;
  private _paramName: string = '';
  private _showArrowButtons: boolean = false;
  private _helpTooltipDefaultText: string = '';
  private _helpTooltipDictKey: string = '';
  private _isProcessingInitialAttributes = false;

  constructor() {
    super();

    this._useShadowDOM = this.hasAttribute('shadow-dom');

    if (this._useShadowDOM) {
      const shadow = this.attachShadow({ mode: 'open', delegatesFocus: true });
      shadow.adoptedStyleSheets = [
        ...shadow.adoptedStyleSheets,
        NumberValueBox.sheet,
      ];
    } else if (!NumberValueBox.lightStyleAppended) {
      const styleEl = document.createElement('style');
      styleEl.textContent = styleText;
      styleEl.setAttribute('data-component', 'as-numbox');
      document.head.appendChild(styleEl);
      NumberValueBox.lightStyleAppended = true;
    }
  }
  
  static get observedAttributes(): string[] {
    return ['value', 'unit', 'decimals', 'padding', 'step', 'min', 'max', 'pad-length', 'show-slider', 'label', 'label-dict-key', 'label-default-text', 'align', 'paramate-paramName', 'paramate-paramname', 'cooloff', 'shadow-dom', 'show-arrow-buttons', 'help-tooltip-default-text', 'help-tooltip-dict-key'];
  }
  
  private parseInitialAttributes(): void {
    // Parse all observed attributes that might be present initially
    const observedAttrs = NumberValueBox.observedAttributes;
    const handled = new Set<string>();

    this._isProcessingInitialAttributes = true;
    try {
      for (const attr of observedAttrs) {
        const normalizedAttr = attr.toLowerCase();
        if (handled.has(normalizedAttr)) continue;

        const actualAttrName = this.hasAttribute(attr)
          ? attr
          : normalizedAttr;

        if (this.hasAttribute(actualAttrName)) {
          const value = this.getAttribute(actualAttrName);
          this.attributeChangedCallback(actualAttrName, null, value);
          handled.add(normalizedAttr);
        }
      }
    } finally {
      this._isProcessingInitialAttributes = false;
    }
  }
  
  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    
    switch (name) {
      case 'value': {
        const parsed = newValue === null || newValue === '' ? 0 : parseFloat(newValue);
        const numericValue = Number.isFinite(parsed) ? parsed : 0;

        if (this._isProcessingInitialAttributes) {
          this._value = numericValue;
          this.updateDisplay();
        } else {
          this.setValue(numericValue, { silenceInputEv: true });
        }
        return;
      }
      case 'unit':
        this._unit = newValue ?? '';
        break;
      case 'decimals':
        this._decimals = parseInt(newValue ?? '2') ?? 2;
        break;
      case 'padding':
        this._padding = (newValue as PaddingType) ?? 'none';
        break;
      case 'step':
        this._step = parseFloat(newValue ?? '1') ?? 1;
        break;
      case 'min':
        this._min = newValue === null ? -Infinity : parseFloat(newValue);
        this.setValue(this._value, { silenceEvents: true });
        break;
      case 'max':
        this._max = newValue === null ? Infinity : parseFloat(newValue);
        this.setValue(this._value, { silenceEvents: true });
        break;
      case 'pad-length':
        this._padLength = parseInt(newValue ?? '0') ?? 0;
        break;
      case 'show-slider':
        this._showSlider = newValue !== null;
        break;
      case 'label':
        this._label = newValue ?? '';
        break;
      case 'align':
        this._align = (newValue as 'left' | 'right') ?? 'right';
        break;
      case 'paramate-paramName':
      case 'paramate-paramname':
        this._paramName = newValue ?? '';
        break;
      case 'cooloff':
        this._cooloff = parseInt(newValue ?? '0') ?? 0;
        break;
      case 'shadow-dom':
        // This attribute is only checked during construction
        break;
      case 'show-arrow-buttons':
        this._showArrowButtons = newValue !== null;
        break;
      case 'help-tooltip-default-text':
        this._helpTooltipDefaultText = newValue ?? '';
        break;
      case 'help-tooltip-dict-key':
        this._helpTooltipDictKey = newValue ?? '';
        break;
    }

    this.updateDisplay();
  }
  
  private render(): void {
    const placement = this.getAttribute("label-placement") ?? "top";

    const html = `
      <div class="as-numbox placement-${placement}${this._showArrowButtons ? ' show-arrow-buttons' : ''}">
        <div class="numbox-label-group"></div>
        <div class="inputs">
          <div class="wrapper">
            <div class="inputs-container">
              <div class="container">
                <input type="number" class="value align-${this._align}">
                <div class="controls">
                  <div class="control-btn increment" tabindex="-1">▲</div>
                  <div class="control-btn decrement" tabindex="-1">▼</div>
                </div>
                <span class="unit"></span>
              </div>
              <div class="slider-container">
                <input type="range" class="slider" tabindex="0">
              </div>
            </div>
          </div>
        </div>
        <div class="light-dom-children"></div>
      </div>
    `;

    if (this._useShadowDOM) {
      this.shadowRoot!.innerHTML = html;
    } else {
      // Preserve light DOM children (like as-hovercard) before replacing innerHTML
      const existingChildren = Array.from(this.children);
      this.innerHTML = html;

      // Move preserved children to the hidden container
      const lightDomContainer = this.querySelector('.light-dom-children');
      if (lightDomContainer) {
        existingChildren.forEach(child => {
          lightDomContainer.appendChild(child);
        });
      }
    }
  }
  
  private setupEventListeners(): void {
    const root = this._useShadowDOM ? this.shadowRoot! : this;
    const container = root.querySelector('.container') as HTMLElement;
    const valueInput = root.querySelector('.value') as HTMLInputElement;
    const incrementBtn = root.querySelector('.increment') as HTMLElement;
    const decrementBtn = root.querySelector('.decrement') as HTMLElement;
    const slider = root.querySelector('.slider') as HTMLInputElement;
    
    // Keyboard events
    // Removed: Keyboard events now handled on valueInput for better tabbing/editing
    
    // Scroll events
    this.hasAttribute("listen-wheel")&&container.addEventListener('wheel', (e: WheelEvent) => {
      e.preventDefault();
      if (e.deltaY < 0) {
        this.increment();
      } else {
        this.decrement();
      }
    });
    
    // Button events
    incrementBtn.addEventListener('click', () => this.increment());
    decrementBtn.addEventListener('click', () => this.decrement());
    
    // Direct input editing
    // No need to toggle readOnly, input is always editable
    valueInput.addEventListener('click', () => {
      valueInput.focus();
    });

    // Select all on double-click
    valueInput.addEventListener('dblclick', () => {
      valueInput.select();
    });
    
    valueInput.addEventListener('blur', () => {
      const newValue = parseFloat(valueInput.value);
      if (!isNaN(newValue)) {
        this.setValue(newValue, { silenceInputEv: true });
      }
      this.updateDisplay();
    });
    
    valueInput.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        const newValue = parseFloat(valueInput.value);
        if (!isNaN(newValue)) {
          this.setValue(newValue);
        }
        valueInput.blur();
      } else if (e.key === 'Escape') {
        this.updateDisplay();
      }
      if(this.hasAttribute("listen-arrow")){
        // Handle increment/decrement with arrow keys and page up/down
        switch (e.key) {
          case 'ArrowUp':
            e.preventDefault();
            this.increment();
            break;
          case 'ArrowDown':
            e.preventDefault();
            this.decrement();
            break;
          case 'PageUp':
            e.preventDefault();
            this.increment(10);
            break;
          case 'PageDown':
            e.preventDefault();
            this.decrement(10);
            break;
        }
      } else {
        // Prevent default arrow key behavior when listen-arrow is not set
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
        }
      }
    });
    
    // Slider events
    slider.addEventListener('input', (e: Event) => {
      const target = e.target as HTMLInputElement;
      const newValue = parseFloat(target.value);
      this.setValue(newValue, { silenceChangeEv: true });
    });
    
    slider.addEventListener('change', (e: Event) => {
      const target = e.target as HTMLInputElement;
      const newValue = parseFloat(target.value);
      // For slider change events, always emit immediately (bypass cooloff)
      this.setValueAndEmitChange(newValue);
    });
    
    // Watch for external changes to the input value (e.g., from Paramate library)
    // Override the value setter to detect when external code changes the input value
    const originalValueDescriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
    const self = this;
    
    Object.defineProperty(valueInput, 'value', {
      get: function() {
        return originalValueDescriptor!.get!.call(this);
      },
      set: function(newVal) {
        const oldVal = originalValueDescriptor!.get!.call(this);
        originalValueDescriptor!.set!.call(this, newVal);
        
        // Check if this was an external change (not from our own updateDisplay)
        if (newVal !== oldVal) {
          const numericValue = parseFloat(newVal);
          if (!isNaN(numericValue) && numericValue !== self._value) {
            // Update internal state without triggering events (to avoid loops)
            self._value = self.clampValue(numericValue);
            self.updateSliderAndDisplay();
          }
        }
      },
      enumerable: originalValueDescriptor!.enumerable,
      configurable: originalValueDescriptor!.configurable
    });
  }
  
  private formatValue(value: number): string {
    let formatted = value.toFixed(this._decimals);
    
    if (this._padding === 'zeros' && this._padLength > 0) {
      // Split into integer and decimal parts
      const parts = formatted.split('.');
      const integerPart = parts[0];
      const decimalPart = parts[1] ?? '';
      
      // Calculate how much space the decimal part takes (including the dot)
      const decimalSpace = this._decimals > 0 ? this._decimals + 1 : 0;
      
      // Pad the integer part to fill the remaining space
      const targetIntegerLength = Math.max(1, this._padLength - decimalSpace);
      const paddedInteger = integerPart.padStart(targetIntegerLength, '0');
      
      // Reconstruct the formatted value
      if (this._decimals > 0) {
        formatted = paddedInteger + '.' + decimalPart;
      } else {
        formatted = paddedInteger;
      }
    } else if (this._padding === 'spaces' && this._padLength > 0) {
      formatted = formatted.padStart(this._padLength, ' ');
    }
    
    return formatted;
  }
  
  private updateSliderAndDisplay(): void {
    const root = this._useShadowDOM ? this.shadowRoot! : this;
    const slider = root.querySelector('.slider') as HTMLInputElement;
    const sliderContainer = root.querySelector('.slider-container') as HTMLElement;

    // Update slider (but not the main input value since that was set externally)
    this.updateSlider(slider, sliderContainer);
  }

  private updateDisplay(): void {
    const root = this._useShadowDOM ? this.shadowRoot! : this;
    const valueInput = root.querySelector('.value') as HTMLInputElement;
    if(!valueInput)return
    const unitSpan = root.querySelector('.unit') as HTMLSpanElement;
    const slider = root.querySelector('.slider') as HTMLInputElement;
    const sliderContainer = root.querySelector('.slider-container') as HTMLElement;
    const labelDiv = root.querySelector('.numbox-label-group') as HTMLElement;
    
    const formattedValue = this.formatValue(this._value);
    valueInput.value = formattedValue;
    unitSpan.textContent = this._unit;

    // Toggle .empty class if unit is empty
    if (!this._unit || this._unit.trim() === "") {
      unitSpan.classList.add("empty");
    } else {
      unitSpan.classList.remove("empty");
    }
    
    // Set width based on content (drives wrapper size)
    this.updateInputWidth(valueInput, formattedValue);
    
    // Update slider
    this.updateSlider(slider, sliderContainer);
    
    // Update label
    this.updateLabel(labelDiv);
    
    // Update Paramate data attribute
    this.updateParamName(valueInput);
  }
  
  private updateLabel(labelDiv: HTMLElement): void {
    const labelText = this._label;
    const labelDictKey = this.getAttribute('label-dict-key');
    const labelDefaultText = this.getAttribute('label-default-text');
    // Use help-placement attribute for hovercard/tooltip placement, default to "right"
    const placement = this.getAttribute('help-placement') ?? 'right';

    // Check for a direct child <as-hovercard> in the light DOM
    let hovercard: HTMLElement | null = null;
    // Only check in light DOM, not shadow
    if (!this._useShadowDOM) {
      // First, check if it's already in the label div (from a previous render)
      const existingWrapper = labelDiv.querySelector('as-hovercard-wrapper');
      if (existingWrapper) {
        hovercard = existingWrapper.querySelector('as-hovercard') as HTMLElement | null;
      }

      // If not found, look in the preserved light DOM children container
      if (!hovercard) {
        const lightDomContainer = this.querySelector('.light-dom-children');
        if (lightDomContainer) {
          hovercard = Array.from(lightDomContainer.children).find(
            (el) => el.tagName && el.tagName.toLowerCase() === 'as-hovercard'
          ) as HTMLElement | null;
        }
      }
    } else if (this.shadowRoot) {
      // If using shadow DOM, check the shadow host's assigned nodes
      hovercard = Array.from(this.childNodes).find(
        (el) =>
          el.nodeType === Node.ELEMENT_NODE &&
          (el as Element).tagName.toLowerCase() === 'as-hovercard'
      ) as HTMLElement | null;
    }

    // Show label and/or help icon if there's label text OR a hovercard/tooltip
    const hasLabel = !!(labelText || labelDictKey || labelDefaultText);
    const hasHelp = !!(hovercard || this._helpTooltipDictKey || this._helpTooltipDefaultText);

    if (hasLabel || hasHelp) {
      const labelContent = hasLabel ? renderLabelContent(labelText, labelDictKey, labelDefaultText) : '';

      if (hovercard) {
        // Remove the hovercard from its current parent to avoid duplication
        if (hovercard.parentElement) {
          hovercard.parentElement.removeChild(hovercard);
        }
        // Render the help icon inside a hovercard wrapper, and insert the hovercard
        labelDiv.innerHTML = `
          ${labelContent}
          <as-hovercard-wrapper placement="${placement}" show-on-hover>
            <span class="help-icon">?</span>
          </as-hovercard-wrapper>
        `;
        // Move the hovercard element into the wrapper
        const wrapper = labelDiv.querySelector('as-hovercard-wrapper');
        if (wrapper) {
          // Always hide the close button in this context
          hovercard.setAttribute('hide-close-button', '');
          wrapper.appendChild(hovercard);
        }
      } else if (this._helpTooltipDictKey || this._helpTooltipDefaultText) {
        const helpTooltipAttrs = [];
        if (this._helpTooltipDictKey) {
          helpTooltipAttrs.push(`dict-key="${this._helpTooltipDictKey}"`);
        }
        if (this._helpTooltipDefaultText) {
          helpTooltipAttrs.push(`default-text="${this._helpTooltipDefaultText}"`);
        }

        labelDiv.innerHTML = `
          ${labelContent}
          <as-tooltip-wrapper placement="${placement}" show-on-hover ${helpTooltipAttrs.join(' ')}>
            <span class="help-icon">?</span>
          </as-tooltip-wrapper>
        `;
      } else {
        labelDiv.innerHTML = labelContent;
      }
    } else {
      labelDiv.innerHTML = '';
    }
  }
  
  private updateParamName(valueInput: HTMLInputElement): void {
    if (this._paramName && this._paramName.trim() !== '') {
      valueInput.setAttribute('data-paramate-paramName', this._paramName);
      valueInput.classList.add('paramateParam');
    } else {
      valueInput.removeAttribute('data-paramate-paramName');
      valueInput.classList.remove('paramateParam');
    }
  }
  

  private updateSlider(slider: HTMLInputElement, container: HTMLElement): void {
    if (this._showSlider) {
      container.classList.add('show');

      // Set slider range - use fixed defaults if min/max not explicitly set
      let min = this._min === -Infinity ? 0 : this._min;
      let max = this._max === Infinity ? 100 : this._max;

      slider.min = min.toString();
      slider.max = max.toString();
      slider.step = this._step.toString();
      slider.value = this._value.toString();
    } else {
      container.classList.remove('show');
    }
  }
  
  private updateInputWidth(input: HTMLInputElement, text: string): void {
    // Check if width is overridden by CSS custom property
    const computedStyle = getComputedStyle(input);
    const customWidth = computedStyle.getPropertyValue('--numbox-input-width').trim();

    if (customWidth && customWidth !== '') {
      input.style.width = customWidth;
      return;
    }

    // Calculate the width needed for the widest possible value
    let widestText = text ?? '0';

    // Always include space for negative sign by testing with a negative version
    const negativeText = '-' + Math.abs(parseFloat(widestText)).toString();

    // If we have a slider with min/max values, consider the widest of those
    if (this._showSlider) {
      const texts = [widestText];

      if (this._min !== -Infinity) {
        texts.push(this.formatValue(this._min));
      }
      if (this._max !== Infinity) {
        texts.push(this.formatValue(this._max));
      }

      // Find the longest text
      widestText = texts.reduce((longest, current) =>
        current.length > longest.length ? current : longest
      );
    }

    // Always test with negative version to reserve space
    const testTexts = [widestText, negativeText];
    const longestText = testTexts.reduce((longest, current) =>
      current.length > longest.length ? current : longest
    );

    // Simple character-based width calculation with buffer
    const totalChars = longestText.length + 1;
    input.style.width = `${totalChars}ch`;
  }
  
  
  private clampValue(value: number): number {
    return Math.max(this._min, Math.min(this._max, value));
  }
  
  private increment(multiplier: number = 1): void {
    const newValue = this.clampValue(this._value + (this._step * multiplier));
    this.setValue(newValue);
  }
  
  private decrement(multiplier: number = 1): void {
    const newValue = this.clampValue(this._value - (this._step * multiplier));
    this.setValue(newValue);
  }
  
  public setValue(value: number, opt?: SetValueOptions): void {
    const { silenceInput, silenceChange } = resolveSetValueSilence(opt);

    const oldValue = this._value;
    const newValue = this.clampValue(value);

    if (oldValue !== newValue) {
      this._value = newValue;
      this.updateDisplay();

      const eventDetail: NumberValueEventDetailBase = {
        type: 'number',
        value: newValue,
        oldValue,
        formattedValue: this.formatValue(newValue),
      };

      if (!silenceInput) {
        this.handleInputEvent(eventDetail);
      }

      if (!silenceChange) {
        this.handleChangeEvent(eventDetail);
      }
    }
  }
  
  private handleInputEvent(eventDetail: NumberValueEventDetailBase): void {
    if (!this._isInInputCooloff) {
      // Send immediately if not in cooloff
      this.emitInputEvent(eventDetail);
      
      if (this._cooloff > 0) {
        this._isInInputCooloff = true;
        
        // Clear any existing timer
        if (this._inputCooloffTimer !== null) {
          clearTimeout(this._inputCooloffTimer);
        }
        
        // Set cooloff timer
        this._inputCooloffTimer = setTimeout(() => {
          this._isInInputCooloff = false;
          this._inputCooloffTimer = null;
          
          // If there's a queued event, emit it with latest value
          if (this._hasQueuedInputEvent && this._queuedEventDetail) {
            this._hasQueuedInputEvent = false;
            this.emitInputEvent(this._queuedEventDetail);
            this._queuedEventDetail = null;
            
            // Restart cooloff cycle
            this.startInputCooloff();
          }
        }, this._cooloff) as unknown as number;
      }
    } else {
      // Queue the event with latest value
      this._hasQueuedInputEvent = true;
      this._queuedEventDetail = eventDetail;
    }
  }
  
  private handleChangeEvent(eventDetail: NumberValueEventDetailBase): void {
    if (!this._isInChangeCooloff) {
      // Send immediately if not in cooloff
      this.emitChangeEvent(eventDetail);
      
      if (this._cooloff > 0) {
        this._isInChangeCooloff = true;
        
        // Clear any existing timer
        if (this._changeCooloffTimer !== null) {
          clearTimeout(this._changeCooloffTimer);
        }
        
        // Set cooloff timer
        this._changeCooloffTimer = setTimeout(() => {
          this._isInChangeCooloff = false;
          this._changeCooloffTimer = null;
          
          // If there's a queued event, emit it with latest value
          if (this._hasQueuedChangeEvent && this._queuedEventDetail) {
            this._hasQueuedChangeEvent = false;
            this.emitChangeEvent(this._queuedEventDetail);
            this._queuedEventDetail = null;
            
            // Restart cooloff cycle
            this.startChangeCooloff();
          }
        }, this._cooloff) as unknown as number;
      }
    } else {
      // Queue the event with latest value
      this._hasQueuedChangeEvent = true;
      this._queuedEventDetail = eventDetail;
    }
  }
  
  private startInputCooloff(): void {
    if (this._cooloff > 0) {
      this._isInInputCooloff = true;
      this._inputCooloffTimer = setTimeout(() => {
        this._isInInputCooloff = false;
        this._inputCooloffTimer = null;
        
        if (this._hasQueuedInputEvent && this._queuedEventDetail) {
          this._hasQueuedInputEvent = false;
          this.emitInputEvent(this._queuedEventDetail);
          this._queuedEventDetail = null;
          this.startInputCooloff();
        }
      }, this._cooloff) as unknown as number;
    }
  }
  
  private startChangeCooloff(): void {
    if (this._cooloff > 0) {
      this._isInChangeCooloff = true;
      this._changeCooloffTimer = setTimeout(() => {
        this._isInChangeCooloff = false;
        this._changeCooloffTimer = null;
        
        if (this._hasQueuedChangeEvent && this._queuedEventDetail) {
          this._hasQueuedChangeEvent = false;
          this.emitChangeEvent(this._queuedEventDetail);
          this._queuedEventDetail = null;
          this.startChangeCooloff();
        }
      }, this._cooloff) as unknown as number;
    }
  }
  
  private emitInputEvent(eventDetail: NumberValueEventDetailBase): void {
    const detailWithPhase: NumberValueInputDetail = {
      ...eventDetail,
      phase: 'input',
    };

    const genericEvent: NumberValueBoxInputEvent = new CustomEvent<NumberValueInputDetail>('as-input-input', {
      detail: detailWithPhase,
      bubbles: true,
      composed: true,
    }) as NumberValueBoxInputEvent;
    this.dispatchEvent(genericEvent);

    // Legacy event for backward compatibility
    const legacyEvent: NumberValueBoxInputEvent = new CustomEvent<NumberValueInputDetail>('as-numbox-input', {
      detail: detailWithPhase,
      bubbles: true,
    }) as NumberValueBoxInputEvent;
    this.dispatchEvent(legacyEvent);
  }
  
  private emitChangeEvent(eventDetail: NumberValueEventDetailBase): void {
    const detailWithPhase: NumberValueChangeDetail = {
      ...eventDetail,
      phase: 'change',
    };

    const genericEvent: NumberValueBoxChangeEvent = new CustomEvent<NumberValueChangeDetail>('as-input-change', {
      detail: detailWithPhase,
      bubbles: true,
      composed: true,
    }) as NumberValueBoxChangeEvent;
    this.dispatchEvent(genericEvent);

    // Legacy event for backward compatibility
    const legacyEvent: NumberValueBoxChangeEvent = new CustomEvent<NumberValueChangeDetail>('as-numbox-change', {
      detail: detailWithPhase,
      bubbles: true,
    }) as NumberValueBoxChangeEvent;
    this.dispatchEvent(legacyEvent);
    
    // Also dispatch standard change event for Paramate integration
    const root = this._useShadowDOM ? this.shadowRoot! : this;
    const valueInput = root.querySelector('.value') as HTMLInputElement;
    if (valueInput && valueInput.classList.contains('paramateParam')) {
      // Make sure the input value reflects the properly formatted value for Paramate
      valueInput.value = detailWithPhase.formattedValue;
      const standardChangeEvent = new Event('change', { bubbles: true });
      valueInput.dispatchEvent(standardChangeEvent);
    }
  }
  
  private setValueAndEmitChange(value: number): void {
    const oldValue = this._value;
    this.setValue(value, { silenceEvents: true });
    const newValue = this._value;

    const eventDetail: NumberValueEventDetailBase = {
      type: 'number',
      value: newValue, 
      oldValue: oldValue,
      formattedValue: this.formatValue(newValue)
    };
    
    this.emitChangeEvent(eventDetail);
  }

  // Getters and setters
  get value(): number {
    return this._value;
  }
  
  set value(val: number) {
    this.setValue(val);
  }
  
  get unit(): string {
    return this._unit;
  }
  
  set unit(val: string) {
    this._unit = val;
    this.updateDisplay();
  }
  
  get decimals(): number {
    return this._decimals;
  }
  
  set decimals(val: number) {
    this._decimals = parseInt(val.toString()) ?? 2;
    this.updateDisplay();
  }
  
  get padding(): PaddingType {
    return this._padding;
  }
  
  set padding(val: PaddingType) {
    this._padding = val;
    this.updateDisplay();
  }
  
  get step(): number {
    return this._step;
  }
  
  set step(val: number) {
    this._step = parseFloat(val.toString()) ?? 1;
    this.updateDisplay();
  }
  
  get min(): number {
    return this._min;
  }
  
  set min(val: number | null) {
    this._min = val === null ? -Infinity : parseFloat(val.toString());
    this.setValue(this._value, { silenceEvents: true });
    this.updateDisplay();
  }
  
  get max(): number {
    return this._max;
  }
  
  set max(val: number | null) {
    this._max = val === null ? Infinity : parseFloat(val.toString());
    this.setValue(this._value, { silenceEvents: true });
    this.updateDisplay();
  }
  
  get padLength(): number {
    return this._padLength;
  }
  
  set padLength(val: number) {
    this._padLength = parseInt(val.toString()) ?? 0;
    this.updateDisplay();
  }
  
  get showSlider(): boolean {
    return this._showSlider;
  }
  
  set showSlider(val: boolean) {
    this._showSlider = val;
    this.updateDisplay();
  }
  
  get label(): string {
    return this._label;
  }
  
  set label(val: string) {
    this._label = val;
    this.updateDisplay();
  }
  
  get align(): 'left' | 'right' {
    return this._align;
  }
  
  set align(val: 'left' | 'right') {
    this._align = val;
    this.updateDisplay();
  }
  
  get cooloff(): number {
    return this._cooloff;
  }
  
  set cooloff(val: number) {
    this._cooloff = parseInt(val.toString()) ?? 0;
  }
  
  connectedCallback(): void {
    this.render();

    // Parse initial attributes after render
    this.parseInitialAttributes();

    this.setupEventListeners();

    // Ensure all attributes are processed before updating display
    requestAnimationFrame(() => {
      this.updateDisplay();
    });
  }
}

customElements.define('as-numbox', NumberValueBox);
